// Error-reporting pieces both runtimes must spell the same way: used by
// `server/observability/sentry.ts`, `app/entry.client.tsx`, and `server/lib/csp.ts` (ingest host).
// Scrubbing is shared so the no-personal-data rule has one copy.
import { PUBLIC_SEGMENTS, segmentSlug } from "#app/lib/shared/links";

// The subset of a Sentry event the scrubber touches. Deliberately not `Partial<Event>`: a field
// absent here is visibly unreviewed.
export type ScrubbableEvent = {
  request?: {
    url?: string;
    query_string?: unknown;
    cookies?: unknown;
    data?: unknown;
    headers?: Record<string, string>;
  };
  breadcrumbs?: { data?: Record<string, unknown> }[];
  user?: { id?: string | number; email?: unknown; username?: unknown; ip_address?: unknown };
  transaction?: string;
  // Transaction events keep the raw incoming URL (query string included) in `trace.data` and span `data`.
  contexts?: { trace?: { data?: Record<string, unknown> } };
  spans?: { data?: Record<string, unknown>; description?: string; name?: string }[];
};

// Request headers allowed out. An allowlist: `cookie`, credentials and `x-forwarded-for` must never leak.
const ALLOWED_HEADERS: readonly string[] = ["user-agent", "content-type", "accept-language"];

// Breadcrumb fields holding a URL with its query string (outbound fetches can carry a credential in the query; `from`/`to` carry visitor filters).
const BREADCRUMB_URL_FIELDS: readonly string[] = ["url", "from", "to"];

// Everything after the first `?` or `#`, gone. Accepts an absolute URL or a bare path.
export function stripQuery(url: string): string {
  return url.split(/[?#]/)[0] ?? "";
}

// An attribute that is only a query string; deleted rather than emptied since there is no safe residue.
const QUERY_ATTRIBUTE = /(^|\.)query(_string)?$/;

// Whether a string is shaped like a URL or root-relative path, and so may carry a query string.
// Tested on the value, not the attribute name, because a list of names breaks when the SDK renames
// one. Other strings (e.g. SQL with `?` placeholders) are left alone.
function isUrlish(value: string): boolean {
  return value.startsWith("/") || /^https?:\/\//i.test(value);
}

// One bag of OpenTelemetry span attributes, cleaned in place.
function scrubAttributes(data: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(data)) {
    if (QUERY_ATTRIBUTE.test(key)) {
      delete data[key];
      continue;
    }

    if (typeof value === "string" && isUrlish(value)) data[key] = stripQuery(value);
  }
}

const SEGMENT_SLUGS: readonly string[] = PUBLIC_SEGMENTS.map(segmentSlug);

// A path reduced to its route template for use as a transaction name, so a `publicId` does not
// mint a group per profile. Narrower than `routeLabel()`: only the three id-bearing shapes in
// `app/routes.ts` collapse.
export function publicTransactionName(pathname: string): string {
  const path = stripQuery(pathname);
  const parts = path.split("/").filter(Boolean);

  if (parts.length === 2 && parts[0] === "arrange-date") return "/arrange-date/:publicId";

  if (parts.length === 3 && parts[0] === "staff" && parts[1] === "curation") {
    return "/staff/curation/:publicId";
  }

  const segment = parts[0];

  if (parts.length === 3 && segment !== undefined && SEGMENT_SLUGS.includes(segment)) {
    if (parts[1] === "profiles") return `/${segment}/profiles/:publicId`;
  }

  return path;
}

// Strips everything an event may not carry out of the process, in place (the SDK's `beforeSend`
// wants the same event back). A backstop, not a licence to put candidate details in exceptions.
export function scrubSentryEvent(event: ScrubbableEvent): void {
  const request = event.request;

  if (request !== undefined) {
    if (request.url !== undefined) request.url = stripQuery(request.url);

    delete request.query_string;
    delete request.cookies;
    // A request body on a public route is a sign-up form: an email, a phone number and a name.
    delete request.data;

    if (request.headers !== undefined) {
      request.headers = Object.fromEntries(
        Object.entries(request.headers).filter(([name]) =>
          ALLOWED_HEADERS.includes(name.toLowerCase()),
        ),
      );
    }
  }

  const user = event.user;

  if (user !== undefined) {
    // An id is an identifier and stays; the other three are the person.
    delete user.email;
    delete user.username;
    delete user.ip_address;
  }

  for (const breadcrumb of event.breadcrumbs ?? []) {
    const data = breadcrumb.data;
    if (data === undefined) continue;

    for (const field of BREADCRUMB_URL_FIELDS) {
      const value = data[field];
      if (typeof value === "string") data[field] = stripQuery(value);
    }
  }

  if (event.transaction !== undefined && event.transaction.startsWith("/")) {
    event.transaction = publicTransactionName(event.transaction);
  }

  // `beforeSendTransaction` runs this same function, so transaction events are scrubbed too.
  const traceData = event.contexts?.trace?.data;
  if (traceData !== undefined) scrubAttributes(traceData);

  for (const span of event.spans ?? []) {
    if (span.data !== undefined) scrubAttributes(span.data);

    // An http client span is described by its URL, which may carry a query credential.
    for (const field of ["description", "name"] as const) {
      const value = span[field];
      if (typeof value === "string" && isUrlish(value)) span[field] = stripQuery(value);
    }
  }
}

// The origin the browser SDK posts to, derived from the DSN so `connect-src` cannot name a wrong
// host. Null for an unparseable DSN means "add nothing": a malformed value must not widen the policy.
export function sentryIngestOrigin(dsn: string): string | null {
  try {
    return new URL(dsn).origin;
  } catch {
    return null;
  }
}

// The `<meta>` name the root route renders the config under; one constant so a rename cannot half-land.
export const SENTRY_CONFIG_META_NAME = "x-marryly-sentry";

// What the server tells the browser SDK, delivered per request because `environment` differs
// across deploys sharing one image. No sample rate: the browser runs no tracing.
export type BrowserSentryConfig = {
  dsn: string;
  environment: string;
  release?: string;
};

// Reads the config from the document, failing closed on a missing or malformed tag.
export function parseBrowserSentryConfig(raw: string | null): BrowserSentryConfig | null {
  if (raw === null || raw === "") return null;

  try {
    const parsed: unknown = JSON.parse(raw);

    if (typeof parsed !== "object" || parsed === null) return null;

    const { dsn, environment, release } = parsed as Record<string, unknown>;

    if (typeof dsn !== "string" || dsn === "") return null;
    if (typeof environment !== "string") return null;

    return { dsn, environment, ...(typeof release === "string" ? { release } : {}) };
  } catch {
    return null;
  }
}
